import { useState, useRef, useCallback } from 'react';

export const useAudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  /** Resolves when MediaRecorder `onstop` has produced the final blob (for reliable submit-after-stop). */
  const pendingStopResolveRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const chunksRef = useRef([]);
  /** Mis à jour de façon synchrone avec MediaRecorder (pas d’attente React) pour un « stop » fiable. */
  const recordingActiveRef = useRef(false);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Setup audio analysis for visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      // Animation loop for audio level
      const updateLevel = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;
          setAudioLevel(average / 255);
        }
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
      
      // Setup media recorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        recordingActiveRef.current = false;
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        const resolve = pendingStopResolveRef.current;
        pendingStopResolveRef.current = null;
        resolve?.(blob);
      };
      
      mediaRecorderRef.current.start();
      recordingActiveRef.current = true;
      setIsRecording(true);
      setAudioBlob(null);
      setAudioUrl(null);
      
    } catch (error) {
      recordingActiveRef.current = false;
      console.error('Error starting recording:', error);
      throw error;
    }
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      const rec = mediaRecorderRef.current;
      // Ref synchrone + état MediaRecorder : évite un stop no-op si `isRecording` React est en retard.
      const live =
        rec &&
        (recordingActiveRef.current || (typeof rec.state === 'string' && rec.state === 'recording'));
      if (!live) {
        resolve(null);
        return;
      }
      pendingStopResolveRef.current = resolve;
      try {
        rec.stop();
      } catch (e) {
        recordingActiveRef.current = false;
        console.error('stopRecording:', e);
        pendingStopResolveRef.current = null;
        setIsRecording(false);
        setAudioLevel(0);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        if (audioContextRef.current) {
          try {
            audioContextRef.current.close();
          } catch {
            /* ignore */
          }
          audioContextRef.current = null;
        }
        resolve(null);
        return;
      }
      setIsRecording(false);
      setAudioLevel(0);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    });
  }, []);

  const resetRecording = useCallback(() => {
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioLevel(0);
  }, []);

  const getBase64Audio = useCallback(async () => {
    if (!audioBlob) return null;
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(audioBlob);
    });
  }, [audioBlob]);

  return {
    isRecording,
    audioBlob,
    audioUrl,
    audioLevel,
    startRecording,
    stopRecording,
    resetRecording,
    getBase64Audio
  };
};
